import React, {
  MutableRefObject,
  useRef,
  useState,
  useContext,
  FC
} from 'react'
import { AppContext } from '../contexts/AppProvider'
import { toast } from 'react-toastify'
import {
  Box,
  IconButton,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Stack,
  Button,
  useTheme
} from '@mui/material'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import BackspaceIcon from '@mui/icons-material/Backspace'
import { useCopyToClipboard } from 'usehooks-ts'
import useIsTouchDevice from '../hooks/useIsTouchDevise'

import NewUnitRequestDialog from './NewUnitRequestDialog'
import SingleTabUsefulButtons from './SingleTabUsefulButtons'
import { Measure, Unit, ConversionResult } from '../types'
import {
  formatConversionValueForReadability,
  getUnitObjectFromUnitLabelForMeasure
} from '../utils/measureHelpers'
import UnitAutocomplete from './UnitAutocomplete'
import { DIRECTION, Direction, urlParamQuantityToConvert } from '../constants'
import SmallPrintTypography from './SmallPrintTypography'
import { useAppNavigate } from '../hooks/useAppNavigate'
import { useSearchParams } from 'react-router-dom'


interface SingleTabContentProps {
  onConversion: (value: string, fromUnit: Unit, toUnit: Unit, measure: Measure, sourceDirection: Direction) => ConversionResult
}


const SingleTabContent: FC<SingleTabContentProps> = ({ onConversion }) => {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const { navigateToConverter } = useAppNavigate()
  const { appState, setAppState } = useContext(AppContext)
  const { currentMeasure } = appState
  const {
    fromUnit,
    toUnit,
    fromNumber,
    toNumber,
    isConversionValid
  } = appState.tabs[currentMeasure.codeName]
  const fromNumberRef = useRef() as MutableRefObject<HTMLInputElement>
  const toNumberRef = useRef() as MutableRefObject<HTMLInputElement>
  const [, copy] = useCopyToClipboard()
  const isTouchDevice = useIsTouchDevice()


  const [openNewUnitRequestDialog, toggleOpenNewUnitRequestDialog] = useState(false)

  /**
   * Swap helper
   */
  const swapUnitsAndNumbers = (): void => {
    const newFromUnit = toUnit
    const newToUnit = fromUnit
    const newFromNumber = toNumber
    const newToNumber = fromNumber

    setAppState({
      ...appState,
      tabs: {
        ...appState.tabs,
        [currentMeasure.codeName]: {
          ...appState.tabs[currentMeasure.codeName],
          fromUnit: newFromUnit,
          toUnit: newToUnit,
          fromNumber: newFromNumber,
          toNumber: newToNumber
        }
      }
    })

    navigateToConverter(currentMeasure, newFromUnit, newToUnit)
  }

  /**
   * Generic number change handler for 'from' or 'to' sourceDirection
   */
  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    sourceDirection: Direction
  ): void => {
    const inputVal = event.target.value

    const { fromNumber, toNumber, isValid } = onConversion(
      inputVal,
      fromUnit,
      toUnit,
      currentMeasure,
      sourceDirection
    )

    setAppState({
      ...appState,
      tabs: {
        ...appState.tabs,
        [currentMeasure.codeName]: {
          ...appState.tabs[currentMeasure.codeName],
          fromNumber,
          toNumber,
          isConversionValid: isValid
        }
      }
    })

    clearQuantityQueryParam()
  }

  const clearQuantityQueryParam = (): void => {
    // Clean up URL from query param which is received from sharing links
    if (searchParams.has(urlParamQuantityToConvert)) {
      navigateToConverter(currentMeasure, fromUnit, toUnit, { replace: true })
    }
  }

  /**
   * Generic unit change handler for 'from' or 'to' sourceDirection
   */
  const handleUnitChange = (newDisplayName: string, sourceDirection: Direction): void => {
    const newUnit: Unit = getUnitObjectFromUnitLabelForMeasure(currentMeasure, newDisplayName)
    const isSameAsOtherSide =
      sourceDirection === DIRECTION.FROM
      ? newUnit === toUnit
      : newUnit === fromUnit

    if (isSameAsOtherSide) {
      toast.info('Swapped Units')
      return swapUnitsAndNumbers()
    }

    if (sourceDirection === DIRECTION.FROM) {
      navigateToConverter(currentMeasure, newUnit, toUnit)
    } else {
      navigateToConverter(currentMeasure, fromUnit, newUnit)
    }
  }

  /**
   * Swap button
   */
  const handleSwapClick = (): void => {
    swapUnitsAndNumbers()
  }

  /**
   * Generic clear handler for 'from' or 'to' sourceDirection
   */
  const handleClearNumber = (
    event: React.MouseEvent<HTMLElement>,
    sourceDirection: Direction
  ): void => {
    setAppState({
      ...appState,
      tabs: {
        ...appState.tabs,
        [currentMeasure.codeName]: {
          ...appState.tabs[currentMeasure.codeName],
          fromNumber: '',
          toNumber: '',
          isConversionValid: false
        }
      }
    })

    const refToFocus = sourceDirection === DIRECTION.FROM ? fromNumberRef : toNumberRef
    if (
      refToFocus?.current.firstElementChild != null &&
      refToFocus.current.firstElementChild.tagName.toLowerCase() === 'input'
    ) {
      // @ts-expect-error
      refToFocus.current.firstElementChild.focus()
    }
  }

  /**
   * Copy button
   */
  const handleClickCopyNumber = async (ref: React.RefObject<HTMLDivElement>): Promise<void> => {
    try {
      // @ts-expect-error
      const copyTargetValue = ref.current.firstElementChild.value
      const isCopiedSuccessfully = await copy(copyTargetValue)

      if (isCopiedSuccessfully) {
        toast.info(`Copied ${copyTargetValue.toString()} to clipboard`)
      } else {
        toast.error('Failed to copy value to clipboard')
      }
    } catch (e) {
      console.log(e)
      toast.error('Failed to copy value to clipboard')
    }
  }

  const onFocusHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
    const inputElement = event.target
    if (isTouchDevice) {
      inputElement.select()
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: '70vh', md: '71.5vh' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          marginBottom: '50px',
          padding: { xs: '0 4px', sm: '0 20px', md: '0 50px', lg: '0 105px' }
        }}
      >
        {/* first column */}
        <Stack
          mt={3.5}
          direction='column'
          spacing={1}
          sx={{
            width: '100%',
            minWidth: { sm: '255px' },
            maxWidth: { xs: '180px', sm: '225px', md: '300px' },
            paddingRight: { xs: '4px', sm: 'unset' }
          }}
        >
          <FormControl>
            <UnitAutocomplete
              label='From Unit'
              autocompleteId='from-unit-autocomplete'
              unit={fromUnit}
              onUnitChange={newUnitName => handleUnitChange(newUnitName, DIRECTION.FROM)}
              onOpenNewUnitRequestDialog={() => toggleOpenNewUnitRequestDialog(true)}
            />
          </FormControl>
          <SmallPrintTypography printValue={fromUnit.displayName} displayOnDesktop={false} />

          <FormControl sx={{ marginTop: '12px!important' }} variant='outlined'>
            <OutlinedInput
              color={isConversionValid ? 'primary' : 'error'}
              value={fromNumber}
              onFocus={onFocusHandler}
              onChange={e => handleNumberChange(e, DIRECTION.FROM)}
              endAdornment={<InputAdornment position='end'>{fromUnit.symbol}</InputAdornment>}
              inputProps={{
                'aria-label': 'number to be converted'
              }}
              ref={fromNumberRef}
            />
          </FormControl>
          <SmallPrintTypography
            printValue={!isConversionValid
              ? '\u00A0'
              : `${formatConversionValueForReadability(fromNumber)} ${fromUnit.symbol}`
            }
            displayOnDesktop={true}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
              marginTop: '10px!important'
            }}
          >
            <Button
              disabled={false}
              size='large'
              variant='outlined'
              startIcon={<BackspaceIcon />}
              onClick={e => handleClearNumber(e, DIRECTION.FROM)}
              sx={{
                border: `1px solid ${theme.palette.grey[500]}`,
                color: `${theme.palette.grey[500]}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[500]}`
                }
              }}
            >
              Clear
            </Button>
            <Button
              disabled={!isConversionValid}
              size='large'
              variant='contained'
              sx={{
                background: theme.palette.custom.beautifulGradient,
                display: { xs: 'none', sm: 'flex' }
              }}
              endIcon={<ContentCopyIcon />}
              onClick={async () => await handleClickCopyNumber(fromNumberRef)}
            >
              Copy
            </Button>
          </Box>
        </Stack>

        {/* second column */}
        <Stack
          mt={3.5}
          direction='column'
          spacing={1}
          sx={{
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <IconButton aria-label='swap measures' onClick={handleSwapClick}>
            <SyncAltIcon sx={{ color: `${theme.palette.grey[500]}` }} fontSize='large' />
          </IconButton>

          <IconButton aria-hidden="true" sx={{ pt: { sm: '20px' } }} disabled>
            <TrendingFlatIcon sx={{ color: `${theme.palette.grey[500]}` }} fontSize='large' />
          </IconButton>

          <Box sx={{ pt: '10px', width: '35px' }}></Box>
        </Stack>
        <Stack
          mt={3.5}
          direction='column'
          spacing={1}
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '0px'
          }}
        >
          <Box sx={{ pt: '161px', width: '0px' }}></Box>
          <Box sx={{ marginLeft: '-25px!important' }}>
            <IconButton aria-label='swap measures' onClick={handleSwapClick}>
              <SyncAltIcon sx={{ color: `${theme.palette.grey[500]}` }} fontSize='large' />
            </IconButton>
          </Box>
        </Stack>

        {/* third column */}
        <Stack
          mt={3.5}
          direction='column'
          spacing={1}
          sx={{
            width: '100%',
            minWidth: { sm: '255px' },
            maxWidth: { xs: '180px', sm: '225px', md: '300px' },
            paddingLeft: { xs: '4px', sm: 'unset' }
          }}
        >
          <FormControl>
            <UnitAutocomplete
              label='To Unit'
              autocompleteId='to-unit-autocomplete'
              unit={toUnit}
              onUnitChange={newUnitName => handleUnitChange(newUnitName, DIRECTION.TO)}
              onOpenNewUnitRequestDialog={() => toggleOpenNewUnitRequestDialog(true)}
            />
          </FormControl>
          <SmallPrintTypography printValue={toUnit.displayName} displayOnDesktop={false} />

          <FormControl sx={{ marginTop: '12px!important' }} variant='outlined'>
            <OutlinedInput
              color={isConversionValid ? 'primary' : 'error'}
              value={toNumber}
              onFocus={onFocusHandler}
              onChange={e => handleNumberChange(e, DIRECTION.TO)}
              endAdornment={<InputAdornment position='end'>{toUnit.symbol}</InputAdornment>}
              inputProps={{
                'aria-label': 'result converted number'
              }}
              ref={toNumberRef}
            />
          </FormControl>
          <SmallPrintTypography
            printValue={!isConversionValid
              ? '\u00A0'
              : `${formatConversionValueForReadability(toNumber)} ${toUnit.symbol}`
            }
            displayOnDesktop={true}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
              marginTop: '10px!important'
            }}
          >
            <Button
              disabled={false}
              size='large'
              variant='outlined'
              startIcon={<BackspaceIcon />}
              onClick={e => handleClearNumber(e, DIRECTION.TO)}
              sx={{
                border: `1px solid ${theme.palette.grey[500]}`,
                color: `${theme.palette.grey[500]}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[500]}`
                },
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              Clear
            </Button>
            <Button
              disabled={!isConversionValid}
              size='large'
              variant='contained'
              onClick={async () => await handleClickCopyNumber(toNumberRef)}
              sx={{
                background: theme.palette.custom.beautifulGradient
              }}
              endIcon={<ContentCopyIcon />}
            >
              Copy
            </Button>
          </Box>
        </Stack>
        <NewUnitRequestDialog open={openNewUnitRequestDialog} setOpen={toggleOpenNewUnitRequestDialog} />
      </Box>
      <SingleTabUsefulButtons />
    </Box>
  )
}

export default SingleTabContent
